import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Calculator, ExternalLink } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';

// NIIT Exposure card for the Year-End Report.
//
// Inputs: filing status + MAGI (Modified Adjusted Gross Income). Both
// persist to /api/users/me/niit-prefs so the user only enters them once.
//
// Compute: NIIT applies to the LESSER of (a) net investment income or
// (b) MAGI minus the filing-status threshold. We use the Year-End
// Report's `forward_annual_income` as a proxy for next-year's projected
// dividend income. Caveat banner explains the Roth/Traditional/HSA
// exemption explicitly so users don't read the number as gospel.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILING_STATUSES = [
  { value: 'single', label: 'Single / HoH', threshold: 200000 },
  { value: 'mfj',    label: 'Married Filing Jointly', threshold: 250000 },
  { value: 'mfs',    label: 'Married Filing Separately', threshold: 125000 },
];
// HoH shares the $200K threshold with Single — keep it implicit in the
// label to avoid a 4th option that confuses casual users.

const fmtUsd = (v, opts = {}) => {
  const { signed = false, decimals = 0 } = opts;
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  if (signed && n > 0) return `+$${abs}`;
  if (n < 0) return `-$${abs}`;
  return `$${abs}`;
};

export default function NiitExposureCard({ projectedDividendIncome, taxableDividendIncome, taxAdvantagedDividendIncome }) {
  const [filingStatus, setFilingStatus] = useState('');
  const [magiInput, setMagiInput] = useState('');
  const [magi, setMagi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Prefer the taxable slice when available — that's the income that
  // actually faces the NIIT surtax. Falls back to total projected
  // income for backwards compatibility with older callers.
  const incomeForNiit = (typeof taxableDividendIncome === 'number')
    ? taxableDividendIncome
    : projectedDividendIncome;

  // Load saved prefs once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/users/me/niit-prefs`, { withCredentials: true });
        if (cancelled) return;
        if (data?.filing_status) setFilingStatus(data.filing_status);
        if (typeof data?.magi === 'number') {
          setMagi(data.magi);
          setMagiInput(String(Math.round(data.magi)));
        }
      } catch {
        // anon / unauth — silently skip; user just won't see prefilled values
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const persist = useCallback(async (patch) => {
    try {
      setSaving(true);
      setSaveError(null);
      await axios.post(`${API}/users/me/niit-prefs`, patch, { withCredentials: true });
    } catch (err) {
      setSaveError(err?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  }, []);

  const onFilingStatusChange = (e) => {
    const v = e.target.value;
    setFilingStatus(v);
    persist({ filing_status: v || null });
  };

  const onMagiBlur = () => {
    const cleaned = magiInput.replace(/[^0-9.]/g, '');
    if (cleaned === '') {
      setMagi(null);
      persist({ magi: null });
      return;
    }
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n < 0) {
      setSaveError('Enter a valid dollar amount');
      return;
    }
    setMagi(n);
    setMagiInput(String(Math.round(n)));
    persist({ magi: n });
  };

  const onMagiKeyDown = (e) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  const threshold = useMemo(() => {
    const fs = FILING_STATUSES.find((s) => s.value === filingStatus);
    return fs?.threshold ?? null;
  }, [filingStatus]);

  const ready = filingStatus && magi !== null && threshold !== null;

  // NIIT exposure math — lesser of net investment income or MAGI excess.
  const calc = useMemo(() => {
    if (!ready) return null;
    const magiExcess = Math.max(0, magi - threshold);
    const dividendIncome = Math.max(0, Number(incomeForNiit) || 0);
    const exposed = Math.min(dividendIncome, magiExcess);
    const surtax = exposed * 0.038;
    let verdict;
    if (magi < threshold) {
      verdict = {
        tone: 'emerald',
        headline: 'Under threshold',
        body: `Your MAGI is $${(threshold - magi).toLocaleString('en-US', { maximumFractionDigits: 0 })} below the ${threshold === 250000 ? 'MFJ' : threshold === 125000 ? 'MFS' : 'Single/HoH'} NIIT threshold. No 3.8% surtax owed on this year's dividends.`,
      };
    } else if (dividendIncome <= magiExcess) {
      verdict = {
        tone: 'rose',
        headline: 'Fully exposed',
        body: `You\u2019re $${magiExcess.toLocaleString('en-US', { maximumFractionDigits: 0 })} over the threshold, which exceeds your projected dividend income. Every dollar of projected dividends faces the 3.8% surtax.`,
      };
    } else {
      verdict = {
        tone: 'amber',
        headline: 'Crosses threshold',
        body: `You\u2019re $${magiExcess.toLocaleString('en-US', { maximumFractionDigits: 0 })} over the threshold. Only that portion of your projected dividend income faces the 3.8% surtax.`,
      };
    }
    return { magiExcess, exposed, surtax, verdict, dividendIncome };
  }, [ready, magi, threshold, incomeForNiit]);

  const toneCls = {
    emerald: 'border-emerald-300/60 bg-emerald-50/40 dark:border-emerald-700/40 dark:bg-emerald-950/20 text-emerald-900 dark:text-emerald-200',
    amber:   'border-amber-300/60   bg-amber-50/40   dark:border-amber-700/40   dark:bg-amber-950/20   text-amber-900   dark:text-amber-200',
    rose:    'border-rose-300/60    bg-rose-50/40    dark:border-rose-700/40    dark:bg-rose-950/20    text-rose-900    dark:text-rose-200',
  };

  return (
    <div
      data-testid="ye-niit-card"
      className="rounded-xl border bg-card p-4 mb-6 print-clean"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-violet-500" aria-hidden="true" />
          <h3 className="text-sm font-bold tracking-tight">
            <AbbrTip term="NIIT">NIIT</AbbrTip> Exposure (3.8% Surtax)
          </h3>
        </div>
        <Link
          to="/learn/niit-surtax"
          className="text-[11px] font-semibold text-violet-600 dark:text-violet-300 hover:underline inline-flex items-center gap-1 no-print"
          data-testid="ye-niit-learn-link"
        >
          What is NIIT? <ExternalLink className="w-3 h-3" aria-hidden="true" />
        </Link>
      </div>

      <p className="text-[11px] text-muted-foreground mb-3">
        The 3.8% Net Investment Income Tax applies on top of regular tax when
        your <AbbrTip term="MAGI"><strong>MAGI</strong></AbbrTip> crosses a filing-status threshold. Enter both
        below to see how much of your projected dividend income would be hit.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3 no-print">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex flex-col gap-1">
          Filing Status
          <select
            value={filingStatus}
            onChange={onFilingStatusChange}
            disabled={loading}
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm font-normal text-foreground normal-case"
            data-testid="ye-niit-filing-status"
          >
            <option value="">— Select —</option>
            {FILING_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label} (${(s.threshold / 1000).toFixed(0)}K)
              </option>
            ))}
          </select>
        </label>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex flex-col gap-1">
          MAGI ($)
          <input
            type="text"
            inputMode="numeric"
            placeholder="e.g. 220000"
            value={magiInput}
            onChange={(e) => setMagiInput(e.target.value)}
            onBlur={onMagiBlur}
            onKeyDown={onMagiKeyDown}
            disabled={loading}
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm font-normal text-foreground normal-case font-mono"
            data-testid="ye-niit-magi-input"
          />
        </label>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex flex-col gap-1">
          Taxable-Account Dividends
          <div
            className="rounded-md border border-dashed border-border bg-muted/30 px-2 py-1.5 text-sm font-mono font-normal text-foreground normal-case"
            data-testid="ye-niit-dividend-display"
            title={typeof taxAdvantagedDividendIncome === 'number' && taxAdvantagedDividendIncome > 0
              ? `Excludes $${Math.round(taxAdvantagedDividendIncome).toLocaleString()} in tax-advantaged accounts (Roth, Traditional IRA, 401(k), HSA)`
              : 'Projected next-year dividend income from your taxable lots only.'}
          >
            {fmtUsd(incomeForNiit)}
          </div>
        </div>
      </div>

      {saveError && (
        <div className="text-[11px] text-rose-600 dark:text-rose-300 mb-2" data-testid="ye-niit-save-error">
          {saveError}
        </div>
      )}

      {!ready && !loading && (
        <div className="rounded-md border border-dashed border-border bg-muted/20 p-3 text-xs text-muted-foreground" data-testid="ye-niit-empty">
          Enter both fields above to see your NIIT exposure for the year.
        </div>
      )}

      {ready && calc && (
        <div className={`rounded-md border-2 p-3 ${toneCls[calc.verdict.tone]}`} data-testid={`ye-niit-verdict-${calc.verdict.tone}`}>
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
            <div className="font-bold text-sm">{calc.verdict.headline}</div>
            <div className="font-mono font-bold text-lg" data-testid="ye-niit-surtax">
              {fmtUsd(calc.surtax, { decimals: 2 })}
              <span className="text-[10px] font-normal ml-1">/yr surtax</span>
            </div>
          </div>
          <p className="text-[11px] leading-snug mb-2">{calc.verdict.body}</p>
          <div className="grid grid-cols-3 gap-2 text-[10px] mt-2 pt-2 border-t border-current/20">
            <div>
              <div className="uppercase tracking-wider opacity-70">MAGI excess</div>
              <div className="font-mono font-bold text-xs" data-testid="ye-niit-excess">{fmtUsd(calc.magiExcess)}</div>
            </div>
            <div>
              <div className="uppercase tracking-wider opacity-70">Exposed income</div>
              <div className="font-mono font-bold text-xs" data-testid="ye-niit-exposed">{fmtUsd(calc.exposed)}</div>
            </div>
            <div>
              <div className="uppercase tracking-wider opacity-70">× 3.8%</div>
              <div className="font-mono font-bold text-xs" data-testid="ye-niit-rate">{fmtUsd(calc.surtax, { decimals: 2 })}</div>
            </div>
          </div>
        </div>
      )}

      <p className="text-[10px] text-muted-foreground italic mt-3 leading-snug">
        {typeof taxAdvantagedDividendIncome === 'number' && taxAdvantagedDividendIncome > 0 ? (
          <>
            <strong className="not-italic text-emerald-700 dark:text-emerald-300">
              Excluded: ${Math.round(taxAdvantagedDividendIncome).toLocaleString()}
            </strong>{' '}
            in tax-advantaged dividends (Roth IRA, Traditional IRA, 401(k), HSA)
            — these are fully exempt from the 3.8% NIIT. The figure above is
            your taxable-account slice only.{' '}
          </>
        ) : (
          <>
            Note: NIIT applies only to dividends in <strong>taxable</strong> accounts.
            Tag each position with its account type on the My Portfolio page so the
            number above auto-excludes Roth/IRA/HSA dividends.{' '}
          </>
        )}
        Not tax advice — see the{' '}
        <Link to="/learn/niit-surtax" className="underline">NIIT guide</Link>{' '}or your
        tax professional. {saving && <span className="ml-1 opacity-60">Saving…</span>}
      </p>
    </div>
  );
}
